import { PressableScale } from "pressto";
import { View, Text, Card } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { type IconNameType, Icon } from "@src/components/Icon";
import { FC, memo } from "react";

interface TimezonePreviewProps {
  timezone: string | null | undefined;
  onPress?: () => void;
  accessoryIcon?: IconNameType;
}

export const TimezonePreview: FC<TimezonePreviewProps> = memo(({ timezone, onPress, accessoryIcon }) => {
  const { theme } = useUnistyles();
  const hasNoTimezone = !timezone;

  // Show warning state if no timezone is configured
  if (hasNoTimezone) {
    const cardContent = (
      <Card
        borderColor="warning"
        py={12}
        style={{
          borderColor: theme.colors.warning[500],
          backgroundColor: theme.colors.warning[50],
        }}
      >
        <View flexDirection="row" alignItems="center" gap="md">
          <View style={{ minWidth: 80 }}>
            <Text textStyle="sm/medium" style={{ color: theme.colors.warning[900] }}>
              Timezone
            </Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }} alignItems="flex-end">
            <View flexDirection="row" alignItems="center" gap="md" justifyContent="flex-end">
              <Icon name="warningOutline" color={theme.colors.warning[600]} />
              <Text
                textStyle="sm/regular"
                numberOfLines={2}
                textAlign="right"
                style={{ color: theme.colors.warning[700], flexShrink: 1 }}
              >
                Not Configured
              </Text>
              {onPress && accessoryIcon && <Icon name={accessoryIcon} color={theme.colors.warning[600]} />}
            </View>
          </View>
        </View>
      </Card>
    );

    if (onPress) {
      return <PressableScale onPress={onPress}>{cardContent}</PressableScale>;
    }

    return cardContent;
  }

  // Show normal timezone state
  const cardContent = (
    <Card py={12}>
      <View flexDirection="row" alignItems="center" gap="md">
        <View style={{ minWidth: 80 }}>
          <Text textStyle="sm/medium">Timezone</Text>
        </View>
        <View style={{ flex: 1, minWidth: 0 }} alignItems="flex-end">
          <View flexDirection="row" alignItems="center" gap="md" justifyContent="flex-end">
            <Text
              color="textMuted"
              textStyle="sm/regular"
              numberOfLines={2}
              textAlign="right"
              style={{ flexShrink: 1 }}
            >
              {timezone}
            </Text>
            {onPress && accessoryIcon && <Icon name={accessoryIcon} color={theme.colors.icon} />}
          </View>
        </View>
      </View>
    </Card>
  );

  if (onPress) {
    return <PressableScale onPress={onPress}>{cardContent}</PressableScale>;
  }

  return cardContent;
});
